import React, { useEffect, useState, useCallback, useMemo } from "react";
import { dragAndDropStyle, thumbStyle } from "../../../../theme/theme";
import { useDropzone } from "react-dropzone";
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import Button from '@material-ui/core/Button';
import UploadFilesTable from "./UploadFilesTables/UploadFilesTable";

function UploadFiles(props) {
  const { thumbsContainer } = thumbStyle();
  const {
    baseStyle,
    activeStyle,
    acceptStyle,
    rejectStyle,
    browserButton,
    span,
    uploadCloudIcon
  } = dragAndDropStyle();

  const {
    question,
    handleFileUpload,
    fileTypes,
    fileMaxSize,
    value,
    maxUploadFiles,
    serviceToUpload,
    serviceToDelete
  } = props;

  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState(value);
  const [rejectedFiles, setRejectedFiles] = useState([]);
  const [allowUpload, setAllowUpload] = useState(true);
  const [uploadError, setUploadError] = useState('');
  const uppercasFileTypes = fileTypes.map(type => type.toUpperCase());
  const finalFileTypes = [...fileTypes, ...uppercasFileTypes];

  useEffect(
    () => () => {
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    }, [files]);

  useEffect(() => {
    handleFileUpload(uploadedFiles, question.id);
    // eslint-disable-next-line
  }, [uploadedFiles]);

  const uploadFileToService = async (file) => {
    if (serviceToUpload) {
      let totalProgress = 0;
      const fileProgress = (progress) => totalProgress = progress;
      const fileUploadPetition = await serviceToUpload(file, fileProgress).catch(error => console.log(error));
      const uploadErrorMessage = fileUploadPetition[0].error;
      if (uploadErrorMessage) {
        setAllowUpload(false);
        setUploadError(uploadErrorMessage);
      } else {
        setAllowUpload(true);
        setUploadedFiles(prevStateFiles => {
          let newArray = [...prevStateFiles];
          let newFile = {
            ...fileUploadPetition[0],
            progress: totalProgress
          }
          newArray.push(newFile);
          return newArray;
        });
      }
    } else {
      return file;
    }
  }

  const deleteFileToService = async (file) => {
    await serviceToDelete(file).catch(error => console.log(error));
  }

  // Remove file value from uploaded files state
  const removeAcceptedFile = (file) => {
    const newFiles = [...files];
    newFiles.splice(newFiles.indexOf(file), 1);
    setFiles(newFiles);
    uploadedFiles.splice(uploadedFiles.findIndex(uploadedFile => uploadedFile.name === file.name), 1);
    deleteFileToService(file);
    handleFileUpload(uploadedFiles, question.id);
  };

  // Remove file value from rejected files state
  const removeRejectedFile = (file) => {
    const newRejectedFiles = [...rejectedFiles];
    newRejectedFiles.splice(newRejectedFiles.indexOf(file), 1);
    setRejectedFiles(newRejectedFiles);
    fileRejections.splice(fileRejections.findIndex(element => element.file === file), 1);
  };

  const onDrop = (droppedFiles) => {
    setFiles(
      droppedFiles.map((file) => {
        return Object.assign(file, {
          preview: URL.createObjectURL(file),
        });
      })
    );
    setRejectedFiles(
      fileRejections.map((element) => {
        return element.file;
      })
    );
  };

  const onDropAccepted = useCallback(
    (acceptedUploadedFiles) => {
      acceptedUploadedFiles.forEach(acceptedFile => {
        if (uploadedFiles.length < maxUploadFiles) {
          uploadFileToService(acceptedFile);
        }
      });
      // eslint-disable-next-line
    }, [uploadedFiles]);

  const fileSizeValidator = (file) => {
    if (file.size / (1024 * 1024) > fileMaxSize) {
      return {
        code: "file-size-too-large",
        message: `File size is bigger than ${fileMaxSize} MB`,
      };
    }
    return null;
  };

  let {
    fileRejections,
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: finalFileTypes,
    maxUploadFiles,
    multiple: false,
    onDrop,
    onDropAccepted,
    validator: fileSizeValidator,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [
      baseStyle,
      activeStyle,
      acceptStyle,
      rejectStyle,
      isDragActive,
      isDragReject,
      isDragAccept,
    ]
  );

  return (
    <React.Fragment>
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <CloudUploadIcon style={uploadCloudIcon} />
        <div style={{ textAlign: "center" }}>
          <h3 style={{ margin: 0, fontSize: "20px" }}>Drag your documents here</h3>
          <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "normal" }}>or</h3>
          <Button variant="contained" style={browserButton}>Browse</Button>
        </div>
        {fileTypes?.length > 0 && (
          <span variant="body1" style={span}>
            Files should be in {`${fileTypes.map(type => type.replace(".", "")).join(", ")}`} with a maximum file size of {`${fileMaxSize} MB.`}
          </span>
        )}
        {fileTypes?.length === 0 && fileMaxSize && (
          <span variant="body1" style={span}>
            Maximum file size of {`${fileMaxSize} MB.`}
          </span>
        )}
        {fileMaxSize && (
          <span variant="body1" style={span}>
            Upload up to {`${maxUploadFiles}`} documents.
          </span>
        )}
      </div>
      <aside className={thumbsContainer}>
        {!allowUpload && <h4>{`${uploadError}`}</h4>}
        <UploadFilesTable
          files={uploadedFiles}
          removeAcceptedFile={removeAcceptedFile}
        />
        <UploadFilesTable
          files={fileRejections}
          removeRejectedFile={removeRejectedFile}
        />
      </aside>
    </React.Fragment>
  );
}

export default UploadFiles;
